import { useState, useEffect } from 'react';
import ClickableSettingRow from '../ClickableSettingRow';
import { SubscriptionSelectionDialog } from '../billing/SubscriptionSelectionDialog';
import { CreditPurchaseDialog } from '../billing/CreditPurchaseDialog';
import { useEntitlements } from '@/hooks/use-entitlements';
import { getPlanById, formatPrice } from '@/types/subscription-plans';
import { getCreditPackBySku, formatCreditPrice } from '@/types/credit-packs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { CreditCard, Coins, Calendar, History, AlertCircle } from 'lucide-react';
import supabase from '@/supabase';
import { useSession } from '@/hooks/useSessionHook';
import type { Database } from '../../../database.types';

type Purchase = Database['public']['Tables']['purchases']['Row'];

interface BillingSettingsProps {
  onNavigate: (view: string) => void;
}

function formatDate(dateString: string | null): string {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function getStatusBadgeVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'active':
    case 'completed':
      return 'default';
    case 'trialing':
      return 'secondary';
    case 'canceled':
    case 'expired':
    case 'failed':
    case 'refunded':
      return 'destructive';
    default:
      return 'outline';
  }
}

export default function BillingSettings({ onNavigate }: BillingSettingsProps) {
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const { subscription, credits, tier, isLoading, refresh } = useEntitlements();
  const { session } = useSession();

  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [purchasesLoading, setPurchasesLoading] = useState(true);
  const [purchasesError, setPurchasesError] = useState<string | null>(null);

  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const isSubscriptionActive = subscription?.status === 'active' || subscription?.status === 'trialing';
  const currentPlan = subscription?.plan ? getPlanById(subscription.plan) : null;

  const pendingPlanId = subscription?.pending_plan;
  const pendingPlan = pendingPlanId ? getPlanById(pendingPlanId as any) : null;
  const hasPendingPlanChange = !!pendingPlanId;

  // Fetch purchase history
  useEffect(() => {
    async function fetchPurchases() {
      if (!session?.user?.id) {
        setPurchasesLoading(false);
        return;
      }

      setPurchasesLoading(true);
      setPurchasesError(null);

      try {
        const { data, error } = await supabase
          .from('purchases')
          .select('*')
          .eq('user_id', session.user.id)
          .order('created_at', { ascending: false })
          .limit(10);

        if (error) {
          throw new Error(error.message);
        }

        setPurchases(data || []);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load purchase history';
        console.error('Purchase history error:', message);
        setPurchasesError(message);
      } finally {
        setPurchasesLoading(false);
      }
    }

    fetchPurchases();
  }, [session?.user?.id]);

  const getSubscriptionLabel = () => {
    if (isLoading) {
      return 'Manage Subscription (Loading...)';
    }
    if (isSubscriptionActive && currentPlan) {
      return `Manage Subscription (Current: ${currentPlan.name} - ${formatPrice(currentPlan.priceInCents)}/mo)`;
    }
    return 'Subscribe';
  };

  const getPurchaseDescription = (purchase: Purchase): string => {
    const creditPack = getCreditPackBySku(purchase.sku as 'credits_10' | 'credits_50' | 'credits_100');
    if (creditPack) {
      return creditPack.name;
    }
    // Check if it's a subscription purchase
    const plan = getPlanById(purchase.sku as 'premium' | 'vip');
    if (plan) {
      return `${plan.name} Subscription`;
    }
    return purchase.sku;
  };

  const handleCancelSubscription = async () => {
    setCancelError(null);
    setCancelLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('cancel-subscription', {
        body: {},
      });

      if (error) throw error;

      console.log('Cancel subscription result:', data);
      await refresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to cancel subscription';
      setCancelError(message);
    } finally {
      setCancelLoading(false);
    }
  };

  return (
    <div className="jr-screen-pad mt-4 flex flex-col gap-4" data-testid="billing-settings">
      <Card className="jr-card gap-4" data-testid="subscription-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base text-[var(--jr-text)]">
            <CreditCard className="h-4 w-4 text-[var(--jr-accent)]" />
            Current Subscription
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-4 w-48" />
            </div>
          ) : isSubscriptionActive && currentPlan ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-semibold">{currentPlan.name}</p>
                  <p className="text-sm text-[var(--jr-text-muted)]">
                    {formatPrice(currentPlan.priceInCents)}/month
                  </p>
                </div>
                <Badge variant={getStatusBadgeVariant(subscription?.status || '')} className="rounded-full" data-testid="subscription-status">
                  {subscription?.status}
                </Badge>
              </div>
              <div className="flex items-center gap-2 text-sm text-[var(--jr-text-muted)]">
                <Calendar className="h-4 w-4 text-[var(--jr-accent)]" />
                <span data-testid="next-billing-date">
                  Next billing: {formatDate(subscription?.current_period_end || null)}
                </span>
              </div>

              {hasPendingPlanChange && pendingPlan && (
                <div className="text-sm text-[var(--jr-text-muted)]" data-testid="pending-plan-change">
                  Plan change pending: <span className="font-medium">{pendingPlan.name}</span> (awaiting provider confirmation)
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-2">
              <p className="text-[var(--jr-text-muted)]">No active subscription</p>
              <p className="mt-1 text-sm text-[var(--jr-text-muted)]">
                Current tier: <span className="capitalize font-medium">{tier}</span>
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="jr-card gap-4" data-testid="credits-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base text-[var(--jr-text)]">
            <Coins className="h-4 w-4 text-[var(--jr-accent)]" />
            Credit Balance
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-8 w-24" />
          ) : (
            <div className="flex items-center justify-between">
              <p className="text-2xl font-bold" data-testid="credit-balance">{credits}</p>
              <span className="text-sm text-[var(--jr-text-muted)]">credits remaining</span>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="jr-card flex flex-col overflow-hidden rounded-[24px]">
        <ClickableSettingRow
          label="Manage Payment Method"
          onClick={() => console.log(`${onNavigate}: Navigate to Payment Method`)}
        />

        <ClickableSettingRow
          label={
            isSubscriptionActive
              ? (cancelLoading ? 'Cancel Subscription (Working...)' : 'Cancel Subscription (effective end of billing period)')
              : 'Cancel Subscription'
          }
          onClick={() => {
            if (!isSubscriptionActive) return;
            void handleCancelSubscription();
          }}
          disabled={!isSubscriptionActive || cancelLoading}
        />

        {cancelError && (
          <div className="px-4 py-2 text-sm text-[color:#efc2bd]">{cancelError}</div>
        )}

        <SubscriptionSelectionDialog
          open={subscriptionDialogOpen}
          onOpenChange={setSubscriptionDialogOpen}
        >
          <div>
            <ClickableSettingRow
              label={getSubscriptionLabel()}
              onClick={() => setSubscriptionDialogOpen(true)}
            />
          </div>
        </SubscriptionSelectionDialog>

        <CreditPurchaseDialog
          open={creditDialogOpen}
          onOpenChange={setCreditDialogOpen}
        >
          <div>
            <ClickableSettingRow
              label={credits > 0 ? `Buy Credits (${credits} remaining)` : 'Buy Credits'}
              onClick={() => setCreditDialogOpen(true)}
              data-testid="buy-credits-row"
            />
          </div>
        </CreditPurchaseDialog>
      </div>

      <Card className="jr-card gap-4" data-testid="purchase-history-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base text-[var(--jr-text)]">
            <History className="h-4 w-4 text-[var(--jr-accent)]" />
            Purchase History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {purchasesLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center justify-between">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-20" />
                </div>
              ))}
            </div>
          ) : purchasesError ? (
            <div className="flex items-center gap-2 text-sm text-[color:#efc2bd]" data-testid="purchase-history-error">
              <AlertCircle className="h-4 w-4" />
              <span>{purchasesError}</span>
            </div>
          ) : purchases.length === 0 ? (
            <p className="py-2 text-center text-sm text-[var(--jr-text-muted)]" data-testid="no-purchases">
              No purchases yet
            </p>
          ) : (
            <div className="space-y-3" data-testid="purchase-list">
              {purchases.map((purchase) => (
                <div
                  key={purchase.id}
                  className="flex items-center justify-between border-b border-[color:var(--jr-border)] py-2 last:border-b-0"
                  data-testid={`purchase-item-${purchase.id}`}
                >
                  <div className="flex-1">
                    <p className="font-medium text-sm">{getPurchaseDescription(purchase)}</p>
                    <p className="text-xs text-[var(--jr-text-muted)]">{formatDate(purchase.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">
                      {formatCreditPrice(purchase.amount)}
                    </span>
                    <Badge variant={getStatusBadgeVariant(purchase.status)} className="text-xs">
                      {purchase.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
